import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Check, Minus, EnvelopeSimple, ShieldCheck, Feather } from "@phosphor-icons/react";

const SALES_EMAIL = "sales@clarocompliance.com";

// [feature, light, shield] — true = included, false = not included, string = detail
const ROWS = [
  ["Plan basics", [
    ["Monthly AI credits", "3,000", "5,000 – 15,000"],
    ["AI attachments per request", "8 files · 40 MB", "Up to 30 files · 100 MB"],
    ["Branded exports (Excel / Word / PDF)", true, true],
    ["Custom login branding", true, true],
  ]],
  ["Standards & readiness", [
    ["Standards Library, CFR viewer & NERC Library", true, true],
    ["STaR Assessment (version-change crosswalks)", true, true],
    ["Readiness Gaps & Required Documents", true, true],
    ["Standards Dev Process (SDP) tracking", false, true],
    ["Readiness Evidence Locker", false, true],
  ]],
  ["Program authoring", [
    ["Policy Manuals", true, true],
    ["Documents (5 P's) & Proposed 5 P's", true, true],
    ["SOPs & Forms — incl. cross-standard SOPs", true, true],
    ["CIP Document Library", true, true],
    ["Internal Controls & Risk Matrix", false, true],
    ["Multi-signer Approvals", false, true],
  ]],
  ["Assessment & audit readiness", [
    ["Inherent Risk Assessment", true, true],
    ["ClaroLens reviews — incl. cross-standard", true, true],
    ["Document Checker & Data Analyzer", true, true],
    ["Evidence Repository (confidential evidence)", false, true],
    ["RSAW Workspace & RFI management", false, true],
    ["Private, entity-specific CFR matrices", false, true],
  ]],
  ["CIP & O&P operations", [
    ["CIP-013 SCRM (supply-chain risk)", true, true],
    ["CIPWatch & OPWatch recurring-obligation trackers", false, true],
    ["CIP-002, CIP-004 access, ClaroPatch (CIP-007/010)", false, true],
    ["PRC-005, EOP-004 & CIP Evidence Request Tool", false, true],
    ["ClaroFlow ticketing", false, true],
  ]],
  ["Enforcement & remediation", [
    ["Sanction Calculator (Appendix 4B)", true, true],
    ["Incident Tracker & Investigations", false, true],
    ["PNC Tracker with one-click Self-Report", false, true],
    ["Auditor Pack & Data & Security Overview", false, true],
  ]],
];

const Cell = ({ v, testid }) => (
  <td className="px-4 py-2.5 text-center" data-testid={testid}>
    {v === true ? <Check size={16} weight="bold" className="inline text-emerald-600" />
      : v === false ? <Minus size={16} className="inline text-slate-300" />
        : <span className="text-xs font-semibold">{v}</span>}
  </td>
);

const PlanHead = ({ icon: Icon, name, blurb, current, testid }) => (
  <th className={`px-4 py-4 text-center align-top w-1/4 ${current ? "bg-primary/[0.06]" : ""}`} data-testid={testid}>
    <Icon size={22} weight="duotone" className="mx-auto mb-1" />
    <div className="font-display font-bold text-base">{name}</div>
    <div className="text-[11px] font-normal text-muted-foreground mt-1 leading-snug">{blurb}</div>
    {current && <div className="mt-2 inline-block text-[10px] uppercase tracking-wide border border-primary text-primary px-1.5" data-testid={`${testid}-current`}>Your plan</div>}
  </th>
);

export default function Plans() {
  const [plan, setPlan] = useState("");
  useEffect(() => { api.get("/tenant/me", { __silent402: true }).then(({ data }) => setPlan(data?.plan || "")).catch(() => {}); }, []);
  const isShield = plan.startsWith("shield");
  const isLight = plan === "clarolight_pro" || plan === "free_trial";
  const mailto = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent("ClaroShield upgrade inquiry")}`;

  return (
    <div data-testid="plans-page">
      <PageHeader title="Compare Plans" subtitle="ClaroShield Light vs ClaroShield — see what each plan includes" />
      <div className="p-4 sm:p-8 max-w-5xl space-y-6">
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="border-b border-border">
              <tr>
                <th className="px-4 py-4 text-left align-bottom text-xs uppercase tracking-wide text-muted-foreground">Feature</th>
                <PlanHead icon={Feather} name="ClaroShield Light" current={isLight} testid="plan-col-light"
                  blurb="Assess, author and track your program from public NERC standards." />
                <PlanHead icon={ShieldCheck} name="ClaroShield" current={isShield} testid="plan-col-shield"
                  blurb="Everything in Light plus confidential evidence and the full audit & enforcement lifecycle." />
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([group, rows]) => [
                <tr key={group} className="bg-secondary/50">
                  <td colSpan={3} className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</td>
                </tr>,
                ...rows.map(([label, l, s], i) => (
                  <tr key={label} className="border-t border-border" data-testid={`plan-row-${group}-${i}`.replace(/[^a-zA-Z0-9-]/g, "-").toLowerCase()}>
                    <td className="px-4 py-2.5">{label}</td>
                    <Cell v={l} />
                    <Cell v={s} />
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 border border-[#071B3E]/20 bg-[#071B3E]/[0.03] px-5 py-4">
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold">{isShield ? "Questions about your plan?" : "Ready for confidential evidence and audit-day tooling?"}</div>
            <div className="text-muted-foreground text-xs mt-0.5">Our team will walk you through ClaroShield and move everything you've built across — nothing is lost.</div>
          </div>
          <Button asChild data-testid="plans-contact-sales-btn" className="rounded-none bg-[#071B3E] hover:bg-[#071B3E]/90">
            <a href={mailto}><EnvelopeSimple size={16} className="mr-1.5" />Contact sales</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
